// Servicio Vortex Academy: cuestionarios en la nueva ruta RTDB.
// Ruta: vortex_academy_quizzes/{jerarquia}/{cuerpo}/{especialidad}/{quizId}
// Los cuestionarios SIEMPRE incluyen retroalimentación (explicacion) en cada pregunta.

import { ref, get, set, onValue } from 'firebase/database';
import { database } from '../firebase';

export const ACADEMY_QUIZZES_ROOT = 'vortex_academy_quizzes';

// Limpia segmentos de ruta para RTDB (evita '/' o '.' problemáticos)
const cleanKey = (value) => {
  if (!value) return 'general';
  return String(value).replace(/[.#$[/\]]/g, '_').trim() || 'general';
};

export const quizPath = ({ jerarquia, cuerpo, especialidad, quizId }) => {
  const base = [
    ACADEMY_QUIZZES_ROOT,
    cleanKey(jerarquia),
    cleanKey(cuerpo),
    cleanKey(especialidad),
  ].join('/');
  return quizId ? `${base}/${cleanKey(quizId)}` : base;
};

// Carga UN cuestionario completo por ruta de perfil + quizId
export const getAcademyQuiz = async ({ jerarquia, cuerpo, especialidad, quizId }) => {
  if (!quizId) return null;
  const snap = await get(ref(database, quizPath({ jerarquia, cuerpo, especialidad, quizId })));
  return snap.exists() ? snap.val() : null;
};

// Carga TODOS los cuestionarios de un perfil (jerarquia/cuerpo/especialidad)
export const getAcademyQuizzes = async ({ jerarquia, cuerpo, especialidad }) => {
  const snap = await get(ref(database, quizPath({ jerarquia, cuerpo, especialidad })));
  if (!snap.exists()) return {};
  const data = snap.val();
  return Object.keys(data).reduce((acc, key) => {
    if (data[key] && typeof data[key] === 'object' && data[key].id) {
      acc[key] = data[key];
    }
    return acc;
  }, {});
};

// Suscripción en tiempo real a los cuestionarios de un perfil
export const subscribeAcademyQuizzes = ({ jerarquia, cuerpo, especialidad }, callback) => {
  const unsubscribe = onValue(
    ref(database, quizPath({ jerarquia, cuerpo, especialidad })),
    (snap) => {
      if (!snap.exists()) return callback({});
      const data = snap.val();
      const quizzes = Object.keys(data).reduce((acc, key) => {
        if (data[key] && typeof data[key] === 'object' && data[key].id) {
          acc[key] = data[key];
        }
        return acc;
      }, {});
      callback(quizzes);
    },
    (err) => {
      console.error('[AcademyQuizzes] Error suscribiéndose:', err);
    }
  );
  return unsubscribe;
};

// Guarda o reemplaza un cuestionario en la ruta de perfil
export const saveAcademyQuiz = async ({ jerarquia, cuerpo, especialidad }, quiz) => {
  if (!quiz || !quiz.id) throw new Error('El cuestionario requiere un id');
  const path = quizPath({ jerarquia, cuerpo, especialidad, quizId: quiz.id });
  const payload = {
    id: quiz.id,
    title: quiz.title || 'Cuestionario Vortex Academy',
    description: quiz.description || '',
    type: quiz.type || 'quiz', // 'quiz' | 'examen' | 'final'
    updatedAt: Date.now(),
    questions: (quiz.questions || []).map((q) => ({
      id: q.id,
      question: q.question,
      options: Array.isArray(q.options) ? q.options : [],
      correct_index: typeof q.correct_index === 'number' ? q.correct_index : 0,
      explanation: q.explanation || '',
    })),
  };
  await set(ref(database, path), payload);
  return payload;
};

// Elimina un cuestionario de un perfil
export const deleteAcademyQuiz = async ({ jerarquia, cuerpo, especialidad, quizId }) => {
  await set(ref(database, quizPath({ jerarquia, cuerpo, especialidad, quizId })), null);
};
